import { z } from 'zod';
import { lenientDateString, lenientNumber } from '../schema-extensions';

/**
 * Validation constants
 */
const VALUE_ENTRY_CONSTANTS = {
  DATE_FORMAT_LENGTH: 10,
  MIN_VALUE: 0,
} as const;

/**
 * Lenient schema for deserializing ValueEntryNode from storage
 */
const ValueEntryNodeDeserializeSchema = z.object({
  date: lenientDateString(''),
  value: lenientNumber(0, VALUE_ENTRY_CONSTANTS.MIN_VALUE)
}).transform(val => ({
  date: val.date,
  value: val.value
}));

/**
 * Strict schema for creating new ValueEntryNode
 */
const ValueEntryNodeCreateSchema = z.object({
  date: z.string().length(VALUE_ENTRY_CONSTANTS.DATE_FORMAT_LENGTH, 'Date must be in YYYY-MM-DD format'),
  value: z.number().min(VALUE_ENTRY_CONSTANTS.MIN_VALUE, 'Value must be non-negative')
}).transform(val => ({
  date: val.date,
  value: val.value
}));

/**
 * ValueEntryNode interface
 * Represents a dated value entry (e.g., for tracking historical values)
 */
export interface ValueEntryNode {
  date: string;
  value: number;
}

/**
 * ValueEntryNode namespace with factory and deserialization methods
 */
export namespace ValueEntryNode {
  export const Constants = VALUE_ENTRY_CONSTANTS;

  export function deserialize(data: unknown): ValueEntryNode {
    return ValueEntryNodeDeserializeSchema.parse(data);
  }

  export function deserializeSafe(data: unknown) {
    const result = ValueEntryNodeDeserializeSchema.safeParse(data);
    return result.success
      ? { success: true, data: result.data }
      : { success: false, error: result.error };
  }

  export function create(data: Partial<ValueEntryNode>): ValueEntryNode {
    return ValueEntryNodeCreateSchema.parse(data);
  }

  export function validate(data: unknown): data is ValueEntryNode {
    return ValueEntryNodeDeserializeSchema.safeParse(data).success;
  }
}
